(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  const drawer = doc.querySelector('[data-cart-drawer]');
  const overlay = doc.querySelector('[data-cart-backdrop]');

  if (!drawer || !overlay || body.dataset.storeMode !== 'live_sales') return;

  const routes = {
    add: body.dataset.cartAddUrl,
    change: body.dataset.cartChangeUrl,
    cart: body.dataset.cartUrl
  };
  const currency = body.dataset.currency || 'TRY';
  const locale = doc.documentElement.lang || 'tr-TR';
  const protectionVariantId = String(drawer.dataset.protectionVariantId || '');
  let lastFocusedElement = null;
  let busy = false;

  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
  }[character]));

  const safeURL = (value = '') => {
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
    } catch (_) {
      return '#';
    }
  };

  const money = cents => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(Number(cents || 0) / 100);

  const toast = message => {
    const element = doc.querySelector('[data-toast]');
    if (!element) return;
    element.textContent = message || body.dataset.addedMessage || 'Added to bag';
    element.classList.add('is-visible');
    window.clearTimeout(element._timer);
    element._timer = window.setTimeout(() => element.classList.remove('is-visible'), 1800);
  };

  const fetchJSON = async (url, options = {}) => {
    const { headers = {}, ...requestOptions } = options;
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...requestOptions,
      headers: {
        Accept: 'application/json',
        ...headers
      }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || body.dataset.cartErrorMessage || 'İşlem tamamlanamadı.');
    }
    return payload;
  };

  const getCart = () => fetchJSON(routes.cart);

  const addItems = items => fetchJSON(routes.add, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items })
  });

  const addForm = form => fetchJSON(routes.add, {
    method: 'POST',
    body: new FormData(form)
  });

  const changeItem = (key, quantity) => fetchJSON(routes.change, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) })
  });

  const setBusy = value => {
    busy = value;
    drawer.toggleAttribute('aria-busy', value);
  };

  const setScrollLocked = value => {
    body.classList.toggle('mv-overflow-hidden', value);
    doc.documentElement.classList.toggle('mv-overflow-hidden', value);
  };

  const focusableElements = () => Array.from(drawer.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter(element => !element.hidden && element.offsetParent !== null);

  const open = async () => {
    if (!drawer.classList.contains('is-open')) {
      lastFocusedElement = doc.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      overlay.classList.add('is-open');
      setScrollLocked(true);
      window.setTimeout(() => drawer.querySelector('[data-cart-close]')?.focus(), 30);
    }
    await refreshCart();
  };

  const close = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    setScrollLocked(false);
    lastFocusedElement?.focus?.();
  };

  const renderLine = item => {
    const imageURL = item.featured_image?.url || item.image || '';
    const image = imageURL
      ? `<img src="${escapeHTML(safeURL(imageURL))}" alt="${escapeHTML(item.product_title || '')}" width="184" height="224" loading="lazy">`
      : '<span>MV</span>';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="mv-cart-item__variant">${escapeHTML(item.variant_title)}</p>`
      : '';
    const isProtection = protectionVariantId && String(item.variant_id) === protectionVariantId;
    const quantity = isProtection ? '' : `
      <div class="mv-cart-item__quantity">
        <button type="button" data-cart-change data-line-key="${escapeHTML(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}">−</button>
        <span aria-live="polite">${Number(item.quantity)}</span>
        <button type="button" data-cart-change data-line-key="${escapeHTML(item.key)}" data-quantity="${Number(item.quantity) + 1}">+</button>
      </div>`;

    return `
      <article class="mv-cart-item" data-cart-line-key="${escapeHTML(item.key)}">
        <a class="mv-cart-item__image" href="${escapeHTML(safeURL(item.url))}" tabindex="-1" aria-hidden="true">${image}</a>
        <div class="mv-cart-item__content">
          <div class="mv-cart-item__title-row">
            <a class="mv-cart-item__title" href="${escapeHTML(safeURL(item.url))}">${escapeHTML(item.product_title).toUpperCase()}</a>
            <strong class="mv-cart-item__price" data-miveka-money data-money-cents="${Number(item.final_line_price) || 0}" data-money-quantity="${Number(item.quantity) || 1}">${escapeHTML(window.MivekaMoney ? window.MivekaMoney.format(item.final_line_price, { quantity: item.quantity }) : money(item.final_line_price))}</strong>
          </div>
          ${variant}
          <div class="mv-cart-item__actions">
            ${quantity}
            <button type="button" class="mv-cart-item__remove" data-cart-remove data-line-key="${escapeHTML(item.key)}">${window.MivekaI18n ? window.MivekaI18n.remove : 'Sil'}</button>
          </div>
        </div>
      </article>`;
  };

  const syncUpsells = cart => {
    const productIds = new Set(cart.items.map(item => String(item.product_id)));
    const cards = Array.from(drawer.querySelectorAll('[data-upsell-card]'));
    cards.forEach(card => {
      card.hidden = productIds.has(String(card.dataset.productId));
    });
    const section = drawer.querySelector('[data-upsell-section]');
    if (section) section.hidden = cards.length === 0 || cards.every(card => card.hidden);
  };

  const syncProtection = cart => {
    const toggle = drawer.querySelector('[data-protection-toggle]');
    if (!toggle || !protectionVariantId) return;
    toggle.checked = cart.items.some(item => String(item.variant_id) === protectionVariantId);
  };

  const renderCart = cart => {
    doc.querySelectorAll('[data-cart-count]').forEach(element => {
      element.textContent = cart.item_count;
    });
    drawer.querySelectorAll('[data-cart-drawer-count]').forEach(element => {
      element.textContent = cart.item_count;
    });

    const items = drawer.querySelector('[data-cart-items]');
    const empty = drawer.querySelector('[data-cart-empty]');
    const summary = drawer.querySelector('[data-cart-summary]');
    const total = drawer.querySelector('[data-cart-total]');
    const isEmpty = cart.items.length === 0;

    if (items) {
      items.hidden = isEmpty;
      items.innerHTML = isEmpty ? '' : cart.items.map(renderLine).join('');
    }
    if (empty) empty.hidden = !isEmpty;
    if (summary) summary.hidden = isEmpty;
    if (total && !window.MivekaMoney) total.textContent = money(cart.total_price);

    syncUpsells(cart);
    syncProtection(cart);

    // Free shipping logic
    const freeShippingThreshold = 21500;
    const progressFill = drawer.querySelector('[data-shipping-fill]');
    const progressText = drawer.querySelector('[data-shipping-text]');
    if (progressFill && progressText) {
      const amountLeft = freeShippingThreshold - cart.total_price;
      const progress = Math.min(100, (cart.total_price / freeShippingThreshold) * 100);
      progressFill.style.width = `${progress}%`;
      const isEN = document.documentElement.lang === 'en' || window.location.pathname.startsWith('/en');
      if (amountLeft > 0) {
        const remainingPrice = `<strong data-miveka-money data-money-cents="${amountLeft}">${escapeHTML(window.MivekaMoney ? window.MivekaMoney.format(amountLeft) : money(amountLeft))}</strong>`;
        progressText.innerHTML = isEN 
          ? `Spend ${remainingPrice} more for free shipping!` 
          : `Ücretsiz kargo için ${remainingPrice} daha harcayın!`;
      } else {
        progressText.innerHTML = isEN ? `You get Free Shipping!` : `Kargo bedava!`;
      }
    }

    if (window.MivekaMoney) window.MivekaMoney.setCart(cart);
    doc.dispatchEvent(new CustomEvent('miveka:cart-updated', { detail: { cart } }));
  };

  const refreshCart = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const cart = await getCart();
      renderCart(cart);
      return cart;
    } catch (error) {
      toast(error.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const updateLine = async (key, quantity) => {
    if (busy || !key) return;
    setBusy(true);
    try {
      const cart = await changeItem(key, quantity);
      renderCart(cart);
    } catch (error) {
      toast(error.message);
      setBusy(false);
      await refreshCart();
    } finally {
      setBusy(false);
    }
  };

  const addVariant = async (variantId, quantity = 1) => {
    if (busy || !variantId) return false;
    setBusy(true);
    try {
      await addItems([{ id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) }]);
      const cart = await getCart();
      renderCart(cart);
      toast();
      return true;
    } catch (error) {
      toast(error.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  doc.addEventListener('click', async event => {
    const openButton = event.target.closest('[data-cart-open]');
    if (openButton) {
      event.preventDefault();
      await open();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      close();
      return;
    }

    const changeButton = event.target.closest('[data-cart-change]');
    if (changeButton && drawer.contains(changeButton)) {
      await updateLine(changeButton.dataset.lineKey, changeButton.dataset.quantity);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton && drawer.contains(removeButton)) {
      await updateLine(removeButton.dataset.lineKey, 0);
      return;
    }

    const upsellButton = event.target.closest('[data-upsell-add]');
    if (upsellButton) {
      upsellButton.disabled = true;
      const added = await addVariant(upsellButton.dataset.variantId, 1);
      upsellButton.disabled = false;
      if (added && !drawer.contains(upsellButton)) await open();
      return;
    }

    const quickAddButton = event.target.closest('[data-quick-add]');
    if (quickAddButton) {
      quickAddButton.disabled = true;
      const added = await addVariant(quickAddButton.dataset.variantId, 1);
      quickAddButton.disabled = false;
      if (added) {
        quickAddButton.closest('dialog')?.close();
        await open();
      }
      return;
    }

    const stickyAddButton = event.target.closest('[data-sticky-add]');
    if (stickyAddButton) {
      const sticky = stickyAddButton.closest('[data-sticky-atc]');
      const variant = sticky?.querySelector('[data-sticky-variant]');
      stickyAddButton.disabled = true;
      const added = await addVariant(variant?.value, 1);
      stickyAddButton.disabled = false;
      if (added) await open();
    }
  });

  overlay.addEventListener('click', close);

  drawer.addEventListener('change', async event => {
    const toggle = event.target.closest('[data-protection-toggle]');
    if (!toggle || !protectionVariantId || busy) return;

    toggle.disabled = true;
    try {
      const cart = await getCart();
      const protectionItem = cart.items.find(item => String(item.variant_id) === protectionVariantId);
      if (toggle.checked && !protectionItem) {
        await addVariant(protectionVariantId, 1);
      } else if (!toggle.checked && protectionItem) {
        await updateLine(protectionItem.key, 0);
      }
    } finally {
      toggle.disabled = false;
    }
  });

  doc.addEventListener('submit', async event => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;

    const submitter = event.submitter;
    if (submitter?.name === 'checkout' || submitter?.closest('.shopify-payment-button')) return;

    event.preventDefault();
    if (busy) return;

    const submitButton = submitter || form.querySelector('[data-add-button]');
    submitButton?.setAttribute('disabled', '');
    setBusy(true);
    try {
      await addForm(form);
      const cart = await getCart();
      renderCart(cart);
      toast();
      await open();
    } catch (error) {
      toast(error.message);
    } finally {
      setBusy(false);
      submitButton?.removeAttribute('disabled');
    }
  });

  doc.addEventListener('keydown', event => {
    if (!drawer.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key !== 'Tab') return;
    const elements = focusableElements();
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  refreshCart();
})();


/* MIVEKA compact option picker: shared desktop/mobile interaction. */
if (!customElements.get('mv-quick-picker')) {
  customElements.define('mv-quick-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.variants = JSON.parse(this.dataset.variants || '[]');
      this.selected = [];
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-picker-open]');
      this.add = this.querySelector('[data-quick-add]');
      this.status = this.querySelector('[data-picker-status]');
      this.price = this.querySelector('[data-picker-price]');
      this.en = document.documentElement.lang.startsWith('en');
      this.opener.addEventListener('click', () => {
        this.dialog.showModal();
        this.update();
      });
      this.querySelector('[data-picker-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', e => {
        if (e.target === this.dialog) {
          const r = this.dialog.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.dialog.close();
        }
        const choice = e.target.closest('[data-picker-choice]');
        if (!choice || choice.disabled) return;
        const index = Number(choice.dataset.index);
        this.selected[index] = choice.dataset.value;
        this.selected.length = index + 1;
        this.update();
      });
      this.update();
    }
    update() {
      this.querySelectorAll('[data-picker-choice]').forEach(button => {
        const index = Number(button.dataset.index);
        const possible = this.variants.some(v => v.available && v.options[index] === button.dataset.value && v.options.every((value, i) => i >= index || !this.selected[i] || this.selected[i] === value));
        button.disabled = !possible;
        button.setAttribute('aria-pressed', String(this.selected[index] === button.dataset.value));
      });
      const variant = this.variants.find(v => v.options.every((value, i) => this.selected[i] === value));
      this.add.disabled = !variant || !variant.available;
      this.add.dataset.variantId = variant?.id || '';
      this.add.textContent = this.en ? 'Add to bag' : 'Sepete ekle';
      this.status.textContent = variant ? (variant.available ? this.selected.join(' / ') : (this.en ? 'Sold out' : 'Bu seçenek tükendi')) : (this.en ? 'Choose your options' : 'Seçeneklerini belirle');
      if (variant) {
        if (window.MivekaMoney) window.MivekaMoney.set(this.price, variant.price);
        else this.price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'tr', {style:'currency',currency:document.body.dataset.currency || 'TRY'}).format(variant.price / 100);
      }
    }
  });
}
